import { Box, Grid, GridItem, Stack } from '@chakra-ui/react';
import type React from 'react';
import FaceliftText from '@/components/Atoms/FaceliftText';
import type { AuditRecord } from '@/types/audit';
import { formatValue, formatDateValue } from '../constants';
import { useColorModeValue } from '@/components/ui/color-mode-utils';

interface AuditDetailsProps {
	data: AuditRecord | null;
	isLoading?: boolean;
}

const AuditDetails: React.FC<AuditDetailsProps> = ({
	data,
	isLoading = false,
}) => {
	// Color mode values for payload/response boxes
	const boxBg = useColorModeValue('gray.50', 'gray.800');
	const boxBorderColor = useColorModeValue('gray.200', 'gray.700');
	const textColor = useColorModeValue('gray.800', 'gray.200');

	if (isLoading) {
		return (
			<Box px={6} py={4}>
				<FaceliftText>Loading...</FaceliftText>
			</Box>
		);
	}

	if (!data) {
		return (
			<Box px={6} py={4}>
				<FaceliftText>No data available</FaceliftText>
			</Box>
		);
	}

	return (
		<Box px={6} py={4}>
			{/* Details Grid - 2 columns */}
			<Grid templateColumns="1fr 1fr" gap={6} mb={6}>
				{/* Left Column */}
				<GridItem>
					<Stack gap={4}>
						{/* User Email */}
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								User Email
							</FaceliftText>
							<FaceliftText fontSize="sm" fontWeight="normal">
								{formatValue(data.userEmail)}
							</FaceliftText>
						</Box>

						{/* Full Name */}
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								Full Name
							</FaceliftText>
							<FaceliftText fontSize="sm" fontWeight="normal">
								{formatValue(data.fullName)}
							</FaceliftText>
						</Box>

						{/* Module */}
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								Module
							</FaceliftText>
							<FaceliftText fontSize="sm" fontWeight="normal">
								{formatValue(data.module)}
							</FaceliftText>
						</Box>

						{/* Action */}
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								Action
							</FaceliftText>
							<FaceliftText fontSize="sm" fontWeight="normal">
								{formatValue(data.action)}
							</FaceliftText>
						</Box>

						{/* Entity Name */}
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								Entity Name
							</FaceliftText>
							<FaceliftText fontSize="sm" fontWeight="normal">
								{formatValue(data.entityName)}
							</FaceliftText>
						</Box>

						{/* Request URL */}
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								Request URL
							</FaceliftText>
							<FaceliftText fontSize="sm" fontWeight="normal" wordBreak="break-all">
								{formatValue(data.requestUrl)}
							</FaceliftText>
						</Box>

						{/* IP Address */}
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								IP Address
							</FaceliftText>
							<FaceliftText fontSize="sm" fontWeight="normal">
								{formatValue(data.ipAddress)}
							</FaceliftText>
						</Box>

							{/* Created At */}
				<Box>
					<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
						Created At
					</FaceliftText>
					<FaceliftText fontSize="sm" fontWeight="normal">
						{formatDateValue(data.createdAt)}
					</FaceliftText>
				</Box>
					</Stack>
				</GridItem>

				{/* Right Column */}
				<GridItem>
					<Stack gap={4}>
						{/* Clinic Name - Show specific clinic or user's multiple clinics */}
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								{data.clinicName && data.clinicName !== 'N/A' ? 'Clinic Name' : 'User Clinics'}
							</FaceliftText>
							<FaceliftText fontSize="sm" fontWeight="normal">
								{data.clinicName && data.clinicName !== 'N/A' 
									? formatValue(data.clinicName)
									: formatValue(data.userClinicNames)?.replace(/,/g, ', ')}
							</FaceliftText>
						</Box>

						{/* Port */}
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								Port
							</FaceliftText>
							<FaceliftText fontSize="sm" fontWeight="normal">
								{formatValue(data.Port)}
							</FaceliftText>
						</Box>

						{/* User Type */}
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								User Type
							</FaceliftText>
							<FaceliftText fontSize="sm" fontWeight="normal">
								{formatValue(data.userType)}
							</FaceliftText>
						</Box>

						{/* OS */}
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								Operating System
							</FaceliftText>
							<FaceliftText fontSize="sm" fontWeight="normal">
								{formatValue(data.OS)}
							</FaceliftText>
						</Box>

						{/* State */}
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								State
							</FaceliftText>
							<FaceliftText fontSize="sm" fontWeight="normal">
								{formatValue(data.state)}
							</FaceliftText>
						</Box>

						{/* Country */}
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								Country
							</FaceliftText>
							<FaceliftText fontSize="sm" fontWeight="normal">
								{formatValue(data.country)}
							</FaceliftText>
						</Box>

						{/* City/Country */}
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								City, Country
							</FaceliftText>
							<FaceliftText fontSize="sm" fontWeight="normal">
								{formatValue(data.city_country)}
							</FaceliftText>
						</Box>
					</Stack>
				</GridItem>
			</Grid>

			{/* Full Width Fields */}
			<Grid templateColumns="1fr" gap={4} mb={4}>
			

				{/* User Agent */}
				<Box>
					<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
						User Agent
					</FaceliftText>
					<FaceliftText fontSize="sm" fontWeight="normal" wordBreak="break-all">
						{formatValue(data.userAgent)}
					</FaceliftText>
				</Box>

				{/* Payload */}
				{(() => {
					if (!data.payload) return null;
					try {
						const parsed = JSON.parse(data.payload);
						// Hide if empty object or empty array
						if (
							(typeof parsed === 'object' && Object.keys(parsed).length === 0) ||
							(Array.isArray(parsed) && parsed.length === 0)
						) {
							return null;
						}
					} catch {
						// If not valid JSON, show it anyway
					}
					return (
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								Payload
							</FaceliftText>
							<Box
								bg={boxBg}
								p={3}
								borderRadius="md"
								border="1px solid"
								borderColor={boxBorderColor}
								maxH="300px"
								overflowY="auto"
							>
								<FaceliftText
									fontSize="xs"
									fontFamily="monospace"
									whiteSpace="pre-wrap"
									wordBreak="break-all"
									color={textColor}
								>
									{(() => {
										try {
											return JSON.stringify(JSON.parse(data.payload), null, 2);
										} catch {
											return data.payload;
										}
									})()}
								</FaceliftText>
							</Box>
						</Box>
					);
				})()}

				{/* Response */}
				{(() => {
					if (!data.response) return null;
					try {
						const parsed = JSON.parse(data.response);
						// Hide if empty object or empty array
						if (
							(typeof parsed === 'object' && Object.keys(parsed).length === 0) ||
							(Array.isArray(parsed) && parsed.length === 0)
						) {
							return null;
						}
					} catch {
						// If not valid JSON, show it anyway
					}
					return (
						<Box>
							<FaceliftText fontSize="sm" fontWeight="bold" fontStyle="italic" mb={1}>
								Response
							</FaceliftText>
							<Box
								bg={boxBg}
								p={3}
								borderRadius="md"
								border="1px solid"
								borderColor={boxBorderColor}
								maxH="300px"
								overflowY="auto"
							>
								<FaceliftText
									fontSize="xs"
									fontFamily="monospace"
									whiteSpace="pre-wrap"
									wordBreak="break-all"
									color={textColor}
								>
									{(() => {
										try {
											return JSON.stringify(JSON.parse(data.response), null, 2);
										} catch {
											return data.response;
										}
									})()}
								</FaceliftText>
							</Box>
						</Box>
					);
				})()}
			</Grid>
		</Box>
	);
};

export default AuditDetails;

